/**
 * Normalization for `order_documents.fields`.
 *
 * Two shapes exist in production:
 *
 * 1. Flat shape (rows created before 2026-03-24):
 *    [
 *      { field_id, section_id, field_title, field_type, value, index, ... },
 *      ...
 *    ]
 *
 * 2. Nested section-block shape (rows created on/after 2026-03-24, introduced
 *    by commit 8dd4d6d3 which auto-populates via fetchDocumentFormStructure +
 *    mergeFieldValues):
 *    [
 *      {
 *        section_id, title, order, type, multiple_elements,
 *        fields: [
 *          { id, label, name, type, value, index },
 *          ...
 *        ]
 *      },
 *      ...
 *    ]
 *
 * The frontend (DocumentDetail, OrderDocumentOutput, PDF generators) was built
 * for the flat shape. This helper flattens nested rows so every consumer can
 * treat the data uniformly. Use it wherever `order_documents.fields` is read.
 */
export const normalizeOrderDocumentFields = (
  fields: any[] | undefined | null
): any[] => {
  if (!fields || !Array.isArray(fields)) return [];

  const looksNested = fields.some(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      Array.isArray(entry.fields) &&
      entry.section_id !== undefined &&
      entry.field_id === undefined
  );
  if (!looksNested) return fields;

  return fields.flatMap((section: any) => {
    if (!section || !Array.isArray(section.fields)) return [];
    return section.fields.map((f: any) => ({
      field_id: f.id,
      field_name: f.name,
      field_title: f.label,
      field_type: f.type,
      section_id: section.section_id,
      section_title: section.title,
      value: f.value,
      index: f.index ?? 0,
    }));
  });
};
